import { AxiosError, isAxiosError } from 'axios'
import { groupBy, isEqual } from 'lodash'
import { defineStore } from 'pinia'

import {
  type ListWorkerConfigurationsParams,
  type WorkerConfigurationCreatePayload,
  type WorkerConfigurationUpdatePayload,
  createWorkerConfiguration,
  listWorkerConfigurationFields,
  listWorkerConfigurations,
  retrieveWorkerConfiguration,
  updateWorkerConfiguration,
} from '@/api'
import { errorParser, isArkindexError } from '@/helpers'
import type { UUID } from '@/types'
import type { WorkerConfiguration, WorkerConfigurationField } from '@/types/workerConfiguration'

import { useNotificationStore } from '.'

export interface EditedField {
  /**
   * Current value of the field.
   *
   * This should not be used by any component other than the field component, unless this value is considered to be valid.
   * This is meaningless for `group` fields.
   */
  value: unknown
  /**
   * Indicates that the field's validation is not complete, thus the real value of `valid` is not yet known.
   */
  loading: boolean
  /**
   * Whether the field has a valid value according to the frontend components.
   *
   * This might not meant the field is valid within this configuration or for a specific worker run,
   * only that no errors were caught early and that creating the config or assigning it to a run may be attempted.
   */
  valid: boolean
  /**
   * Errors returned by the Arkindex API for this field, to be displayed by the field.
   *
   * This does not include frontend validation errors, which are managed only at the component level.
   */
  errors: string[]
}

interface State {
  fields: { [versionId: UUID]: WorkerConfigurationField[] }

  configurations: {
    [workerId: UUID]: {
      [workerConfigurationId: UUID]: WorkerConfiguration
    }
  }

  /**
   * ID of the WorkerVersion whose fields are used to edit the current configuration.
   */
  targetVersionId: UUID | null

  /**
   * When editing a clone of an existing configuration, the ID of the original configuration.
   */
  originalConfigurationId: UUID | null

  /**
   * The configuration being edited in a form.
   *
   * The keys match the keys found in the final configuration that would be sent to a WorkerRun, with the group prefixes added.
   * Groups are also included, even though they would not be sent to a WorkerRun directly.
   */
  editedConfiguration: {
    [key: string]: EditedField
  }
}

export type GroupedWorkerConfigurationField = WorkerConfigurationField & {
  children: WorkerConfigurationField[]
}

export const useWorkerConfigurationStore = defineStore('workerConfiguration', {
  state: (): State => ({
    fields: {},
    configurations: {},
    targetVersionId: null,
    originalConfigurationId: null,
    editedConfiguration: {},
  }),
  actions: {
    async list(workerId: UUID, { page = 1, ...options }: ListWorkerConfigurationsParams) {
      if (page === 1) this.configurations[workerId] = {}
      try {
        const data = await listWorkerConfigurations(workerId, { page, ...options })

        this.configurations[workerId] = {
          ...this.configurations[workerId],
          ...Object.fromEntries(
            data.results.map((configuration) => [configuration.id, configuration]),
          ),
        }

        if (!data || !data.number || page !== data.number) {
          // Avoid any loop
          throw new Error(
            `Pagination failed listing worker configurations for worker "${workerId}"`,
          )
        }
        // Load other pages
        if (data.next) this.list(workerId, { page: page + 1, ...options })
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 403) {
          throw err
        } else {
          useNotificationStore().notify({ type: 'error', text: errorParser(err) })
        }
      }
    },

    async get(workerId: UUID, configurationId: UUID) {
      const resp = await retrieveWorkerConfiguration(configurationId)
      if (workerId in this.configurations) this.configurations[workerId][resp.id] = resp
      else this.configurations[workerId] = { [resp.id]: resp }
    },

    async create(workerId: UUID, configuration: WorkerConfigurationCreatePayload) {
      let resp
      try {
        resp = await createWorkerConfiguration(workerId, configuration)
      } catch (err) {
        throw this.extractConfigurationErrors(err)
      }

      if (workerId in this.configurations) this.configurations[workerId][resp.id] = resp
      else this.configurations[workerId] = { [resp.id]: resp }
      return resp
    },

    /**
     * On an error, if the error can be parsed as an Arkindex validation error,
     * extract the messages specific to some configuration fields and save them in this store,
     * then return the error without those messages so that it can be re-thrown.
     * This allows advanced display of backend errors in the configuration form.
     * @param err Anything caught by a `catch` block.
     * @returns A new error with the error messages filtered out, or the original error if it could not be parsed.
     */
    extractConfigurationErrors(err: unknown): unknown {
      // When editing a configuration with configuration fields, we might be able to handle some errors specific to each field
      if (
        isArkindexError(err) &&
        err.response.status === 400 &&
        this.targetVersionId &&
        Object.keys(this.editedConfiguration).length
      ) {
        // Reset any previous errors
        for (const field of Object.values(this.editedConfiguration)) {
          field.errors = []
        }

        const unparsed = []
        for (const error of err.response.data.errors) {
          // Only handle errors for `configuration.[field name]`
          if (
            !error.attr ||
            error.attr[0] !== 'configuration' ||
            typeof error.attr[1] !== 'string' ||
            !(error.attr[1] in this.editedConfiguration)
          ) {
            unparsed.push(error)
            continue
          }
          this.editedConfiguration[error.attr[1]].errors.push(error.detail)
        }

        // Rethrow the same error, but with just the errors that we could not parse here, so that they can be handled differently by the components
        return AxiosError.from(err, err.code, err.config, err.request, {
          ...err.response,
          data: { ...err.response.data, errors: unparsed },
        })
      }
      return err
    },

    async update(workerId: UUID, configurationId: UUID, data: WorkerConfigurationUpdatePayload) {
      /*
       * Keep the current archived attribute of the configuration, to see if the backend will update it.
       * When the backend changes the archived state, we need to remove the configuration from the list
       * in the store: since we only list configurations that are either archived or not, and never
       * both at once, the configuration should disappear from the list when it switches state.
       * Doing so ourselves, without calling listConfigurations again, is cleaner and avoids stale reads.
       */
      const currentArchivedState = this.configurations[workerId]?.[configurationId]?.archived

      let resp
      try {
        resp = await updateWorkerConfiguration(configurationId, data)
      } catch (err) {
        useNotificationStore().notify({ type: 'error', text: errorParser(err) })
        return
      }

      if (currentArchivedState !== undefined && resp.archived !== currentArchivedState) {
        delete this.configurations[workerId][configurationId]
      } else {
        this.configurations[workerId][configurationId] = resp
      }
    },

    /**
     * Prepare `editedConfiguration` to edit a new worker configuration from the worker configuration fields of a specified worker version.
     */
    async editNew(versionId: UUID) {
      if (!this.fields[versionId]) await this.listFields(versionId)
      if (!this.fields[versionId].length)
        throw new Error('No worker configuration fields are defined for this worker version')

      this.targetVersionId = versionId
      this.originalConfigurationId = null
      this.editedConfiguration = Object.fromEntries(
        Object.entries(this.fieldsWithPrefix(versionId)!).map(([key, field]) => [
          key,
          {
            value: field.default,
            /*
             * When the field is not editable, it might not be shown in the configuration form,
             * so the field validation might not run on that field.
             * If it doesn't run, then `valid` is never updated, so using the safe `loading: true, valid: false`
             * would make the configuration invalid forever.
             * We thus assume that all non-editable fields are already valid and not loading.
             */
            loading: field.editable,
            valid: !field.editable,
            errors: [],
          },
        ]),
      )
    },

    /**
     * Prepare `editedConfiguration` to edit a new worker configuration by cloning an existing one.
     */
    async editClone(workerId: UUID, versionId: UUID, configurationId: UUID) {
      if (!this.fields[versionId]) await this.listFields(versionId)
      if (!this.fields[versionId].length)
        throw new Error('No worker configuration fields are defined for this worker version')

      if (!this.configurations[workerId]?.[configurationId])
        await this.get(workerId, configurationId)
      const previousConfig = this.configurations[workerId][configurationId].configuration

      this.targetVersionId = versionId
      this.originalConfigurationId = configurationId
      this.editedConfiguration = Object.fromEntries(
        Object.entries(this.fieldsWithPrefix(versionId)!).map(([key, field]) => {
          const value = previousConfig[key] ?? field.default
          /*
           * Just like in editNew, we must mark non-editable fields as valid by default.
           * However, the previous configuration might have had a value different than the field's constant value,
           * so we declare non-editable fields with unexpected values to be invalid.
           */
          const valid = field.editable ? false : isEqual(value, field.default)
          return [
            key,
            {
              value,
              valid,
              loading: field.editable,
              errors: [],
            },
          ]
        }),
      )
    },

    cancelEdit() {
      this.targetVersionId = null
      this.originalConfigurationId = null
      this.editedConfiguration = {}
    },

    async listFields(id: UUID) {
      if (this.fields[id]) return
      this.fields[id] = await listWorkerConfigurationFields(id)
    },
  },
  getters: {
    /**
     * Whether any field in the currently edited configuration is busy validating.
     */
    isLoading(): boolean {
      return Object.values(this.editedConfiguration).some((field) => field.loading)
    },

    /**
     * Whether all fields in the currently edited configuration are validated.
     *
     * The loading state must be checked separately.
     */
    isValid(): boolean {
      const values = Object.values(this.editedConfiguration)
      return values.length > 0 && values.every((field) => field.valid)
    },

    /**
     * The result of a configuration form, the values that can be sent to `CreateWorkerConfiguration` as the `configuration`.
     */
    generatedConfiguration(): Record<string, unknown> {
      if (!this.targetVersionId || !this.editedConfiguration) return {}
      const fields = this.fieldsWithPrefix(this.targetVersionId)
      if (!fields) return {}
      return Object.fromEntries(
        Object.entries(fields)
          .filter(([, field]) => field.type !== 'group')
          .map(([key]) => {
            if (!this.editedConfiguration[key])
              throw new Error(`Field ${key} not found in edited configuration`)
            return [key, this.editedConfiguration[key].value]
          }),
      )
    },

    /**
     * WorkerConfigurationFields of a single worker version grouped by parent `group` field.
     */
    groupedFields() {
      return (versionId: UUID): GroupedWorkerConfigurationField[] | null => {
        if (!this.fields[versionId]) return null
        const fieldsByParent = groupBy(this.fields[versionId], (field) => field.parent_id)
        return this.fields[versionId]
          .filter((field) => !field.parent_id)
          .map((field) => ({
            ...field,
            children: field.type === 'group' ? fieldsByParent[field.id] : [],
          }))
      }
    },

    /**
     * WorkerConfigurationFields of a single worker version, mapped by key with the group prefix.
     *
     * For example, a field `child` in a group named `group` will be returned as `{ "group.child": field }`.
     */
    fieldsWithPrefix() {
      return (versionId: UUID): Record<string, WorkerConfigurationField> | null => {
        if (!this.fields[versionId]) return null
        const fields = Object.fromEntries(this.fields[versionId].map((field) => [field.id, field]))
        return Object.fromEntries(
          Object.values(fields).map((field) => {
            if (!field.parent_id) return [field.key, field]
            if (!fields[field.parent_id])
              throw new Error(`Parent field ${field.parent_id} not found`)
            return [`${fields[field.parent_id].key}.${field.key}`, field]
          }),
        )
      }
    },
  },
})
